import {
  resolveSahDeliveryCheckHref,
  type SahDeliveryCheckHrefContext,
} from '@statura/utils/sah-readiness-resolver';
import type { SahReadinessCheckCode } from '@statura/utils';
import {
  carePlanComposeHref,
  type ClientCarePlanReturnContext,
} from '@/lib/sah/client-care-plan-navigation';
import {
  resolveSahCarePlanDestination,
  isSahCarePlanRecordStatus,
} from '@/lib/sah/sah-care-plan-destination';
import {
  deliveryCheckComposerAnchor,
  DELIVERY_CHECK_COMPOSE_ANCHOR,
} from '@/lib/care-plans/composer-anchor-navigation';
import { resolveAcknowledgementBlockerForStatus } from '@/lib/care-plans/care-plan-acknowledgement-resolution';

export type SahDeliveryCheckResolveRoute = {
  href: string;
  label: string;
};

const DELIVERY_CHECK_LABELS: Partial<Record<SahReadinessCheckCode | string, string>> = {
  ACTIVE_SUPPORT_AT_HOME_PLAN: 'Activate',
  ACKNOWLEDGED_CARE_PLAN: 'Plan review',
  DATE_VALID_SERVICE_INCLUSIONS: 'Services',
  SIGNED_SERVICE_AGREEMENT: 'Agreement',
  GENERAL_CARE_CONSENT: 'Consent',
  CURRENT_QUARTER_BUDGET: 'Budget',
  HCP_OPENING_RECORDED: 'HCP opening',
  SAH_MEANS_BASIS: 'Contribution',
};

function intentForDeliveryCheck(code: string) {
  switch (code) {
    case 'ACTIVE_SUPPORT_AT_HOME_PLAN':
      return 'delivery' as const;
    case 'ACKNOWLEDGED_CARE_PLAN':
    case 'SIGNED_SERVICE_AGREEMENT':
      return 'agreements' as const;
    case 'DATE_VALID_SERVICE_INCLUSIONS':
      return 'services' as const;
    case 'CURRENT_QUARTER_BUDGET':
    case 'HCP_OPENING_RECORDED':
    case 'SAH_MEANS_BASIS':
      return 'budget' as const;
    case 'GENERAL_CARE_CONSENT':
      return 'continue' as const;
    default:
      return 'continue' as const;
  }
}

/**
 * Status-aware delivery-check href resolver for web surfaces.
 * ACTIVE/COMPLETED SAH plans open record/budget/client anchors; drafts use compose.
 */
export function resolveSahDeliveryCheckNavigationRoute(input: {
  code: SahReadinessCheckCode | string;
  clientId: string;
  carePlanId?: string | null;
  planStatus?: string | null;
  isSupportAtHomePlan?: boolean;
  returnContext?: ClientCarePlanReturnContext;
  preferClientSurfaces?: boolean;
}): SahDeliveryCheckResolveRoute {
  const label = DELIVERY_CHECK_LABELS[input.code] ?? 'Resolve';
  const ctx: SahDeliveryCheckHrefContext = {
    clientId: input.clientId,
    carePlanId: input.carePlanId,
    preferClientSurfaces: input.preferClientSurfaces ?? true,
  };

  const planStatus = input.planStatus;
  const planId = input.carePlanId;
  const useRecordSurface =
    input.isSupportAtHomePlan &&
    planId &&
    isSahCarePlanRecordStatus(planStatus) &&
    ![
      'CURRENT_QUARTER_BUDGET',
      'HCP_OPENING_RECORDED',
      'SAH_MEANS_BASIS',
      'GENERAL_CARE_CONSENT',
    ].includes(String(input.code));

  if (useRecordSurface) {
    const destination = resolveSahCarePlanDestination({
      clientId: input.clientId,
      plan: { id: planId, status: planStatus },
      intent: intentForDeliveryCheck(String(input.code)),
      returnContext: input.returnContext,
    });
    return { href: destination.href, label };
  }

  if (input.isSupportAtHomePlan && planId && !isSahCarePlanRecordStatus(planStatus)) {
    const hash =
      String(input.code) === 'ACKNOWLEDGED_CARE_PLAN'
        ? resolveAcknowledgementBlockerForStatus(planStatus).hash
        : (deliveryCheckComposerAnchor(String(input.code), planStatus) ??
          DELIVERY_CHECK_COMPOSE_ANCHOR[String(input.code)]);
    if (hash) {
      const routeLabel =
        String(input.code) === 'ACKNOWLEDGED_CARE_PLAN' &&
        String(planStatus ?? '').toUpperCase() === 'DRAFT'
          ? resolveAcknowledgementBlockerForStatus(planStatus).actionLabel
          : label;
      return {
        href: carePlanComposeHref(planId, { hash, returnContext: input.returnContext }),
        label: routeLabel,
      };
    }
  }

  return {
    href: resolveSahDeliveryCheckHref(input.code, ctx),
    label,
  };
}

export function buildSahDeliveryCheckNavigationRoutes(input: {
  codes: readonly { code: string }[];
  clientId: string;
  carePlanId?: string | null;
  planStatus?: string | null;
  isSupportAtHomePlan?: boolean;
  returnContext?: ClientCarePlanReturnContext;
}): Record<string, SahDeliveryCheckResolveRoute> {
  return Object.fromEntries(
    input.codes.map((check) => [
      check.code,
      resolveSahDeliveryCheckNavigationRoute({
        code: check.code,
        clientId: input.clientId,
        carePlanId: input.carePlanId,
        planStatus: input.planStatus,
        isSupportAtHomePlan: input.isSupportAtHomePlan,
        returnContext: input.returnContext,
      }),
    ]),
  );
}
